import { useInfiniteQuery, UseInfiniteQueryOptions, useMutation } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetcher } from '@/api/fetcher';
import {
  ActivityLogsError,
  ActivityLogsRequest,
  ActivityLogsResponse,
  PostActivityLogError,
  PostActivityLogRequest,
  PostActivityLogResponse,
} from '@/api/model';
import { removeEmptyParams } from '@/api/utils';

const KEY = {
  GET_ACTIVITY_LOGS: 'GET_ACTIVITY_LOGS',
} as const;

const request = {
  getActivityLogs: async ({ payload: { params } }: ActivityLogsRequest, signal?: AbortSignal) => {
    const { data } = await fetcher.FCT.get<ActivityLogsResponse>(ENDPOINTS.FCT.ACTIVITY_LOG, {
      params: removeEmptyParams(params),
      signal,
    });

    return data;
  },
  postActivityLogs: async ({ payload }: PostActivityLogRequest) => {
    const { data } = await fetcher.FCT.post<PostActivityLogResponse>(
      ENDPOINTS.FCT.ACTIVITY_LOG,
      payload,
    );

    return data;
  },
};

const query = {
  useGetActivityLogs: (
    { payload }: ActivityLogsRequest,
    config?: UseInfiniteQueryOptions<ActivityLogsResponse, ActivityLogsError>,
  ) =>
    useInfiniteQuery({
      enabled: !!payload.params.report_uuid,
      getNextPageParam: (lastPage) => (lastPage.items.length > 0 ? lastPage.page + 1 : undefined),
      initialPageParam: 0,
      queryFn: ({ pageParam = 0, signal }) =>
        request.getActivityLogs(
          { payload: { params: { ...payload.params, page: pageParam as number } } },
          signal,
        ),
      queryKey: [KEY.GET_ACTIVITY_LOGS, payload.params.study_id, payload.params.report_uuid],
      ...config,
    }),
};

const mutation = {
  usePostActivityLogs: () =>
    useMutation<PostActivityLogResponse, PostActivityLogError, PostActivityLogRequest>({
      mutationFn: request.postActivityLogs,
    }),
};

export const activityLogs = {
  KEY,
  mutation,
  query,
  request,
};
